import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';
import type { ReactNode } from 'react';
import styles from './CdTabs.module.scss';

export interface CdTabItem {
  id: string;
  label: string;
  /** Panel shown for this tab. Omit on every tab to render just the tab list. */
  content?: ReactNode;
}

export interface CdTabsProps {
  label: string;
  tabs: CdTabItem[];
  selectedId: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * Carbon tab navigation with controlled selection. Renders a panel per tab when tabs have
 * `content`; without it, only the tab list renders and the page shows the active view.
 */
export const CdTabs = ({ label, tabs, selectedId, onChange, className }: CdTabsProps) => {
  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === selectedId),
  );

  return (
    <div className={[styles.tabs, className].filter(Boolean).join(' ')}>
      <Tabs
        selectedIndex={selectedIndex}
        onChange={({ selectedIndex: nextIndex }) => {
          const nextTab = tabs[nextIndex];
          if (nextTab) onChange(nextTab.id);
        }}
      >
        <TabList aria-label={label} contained>
          {tabs.map((tab) => (
            <Tab key={tab.id}>{tab.label}</Tab>
          ))}
        </TabList>
        {tabs.some((tab) => tab.content !== undefined) && (
          <TabPanels>
            {tabs.map((tab) => (
              <TabPanel key={tab.id}>{tab.content}</TabPanel>
            ))}
          </TabPanels>
        )}
      </Tabs>
    </div>
  );
};
